import React, { useState, useEffect, useCallback } from 'react';
import { useBlocker, Location } from 'react-router-dom';
import ConfirmDialog from '../components/dialog/ConfirmDialog';

function useBeforeUnload(shouldBlock: boolean, message: string = "You have unsaved changes. Are you sure you want to leave?") {
  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (shouldBlock) {
        event.preventDefault();
        event.returnValue = message; // Standard for browser confirmation
        return message; // For some older browsers
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [shouldBlock, message]);
}

function deepEqual(obj1: any, obj2: any): boolean {
  if (obj1 === obj2) {
    return true;
  }

  if (typeof obj1 !== 'object' || obj1 === null || typeof obj2 !== 'object' || obj2 === null) {
    return false;
  }

  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);

  if (keys1.length !== keys2.length) {
    return false;
  }

  for (const key of keys1) {
    if (!keys2.includes(key) || !deepEqual(obj1[key], obj2[key])) {
      return false;
    }
  }

  return true;
}

interface UseFormUnsavedChangesProps<T extends object> {
  isDirty?: boolean;
  formData?: T;
  initialFormData?: T;
  onConfirmLeave?: () => void;
  confirmationMessage?: string;
  dialogTitle?: string;
  dialogConfirmText?: string;
  dialogCancelText?: string;
  routesToBlock?: string[]; // NEW: Routes that should trigger the block
}

interface UseFormUnsavedChangesResult {
  isDirty: boolean;
  ConfirmLeaveDialog: React.FC;
}

export function useFormUnsavedChanges<T extends object>({
  isDirty,
  formData,
  initialFormData,
  onConfirmLeave,
  confirmationMessage = "You have unsaved changes that will be lost if you leave this page.",
  dialogTitle = "Discard Changes?",
  dialogConfirmText = "Discard and Leave",
  dialogCancelText = "Stay on Page",
  routesToBlock = [],
}: UseFormUnsavedChangesProps<T>): UseFormUnsavedChangesResult {

  const isFormDirty = isDirty || !deepEqual(formData, initialFormData);

  // Handle browser's beforeunload event (still native prompt)
  useBeforeUnload(isFormDirty, confirmationMessage);

  const blocker = useBlocker(
    useCallback(
      ({ nextLocation }: { nextLocation: Location }) => {
        if (isFormDirty) {
          return routesToBlock.some(blockedRoute =>
            nextLocation.pathname.toLowerCase().startsWith(blockedRoute.toLowerCase())
          );
        }
        return false;
      },
      [isFormDirty, routesToBlock]
    )
  );


  const [openDialog, setOpenDialog] = useState(false);

  useEffect(() => {
    if (blocker.state === 'blocked' && isFormDirty) { // Ensure dialog only shows if actually dirty
      setOpenDialog(true);
    } else if (blocker.state === 'unblocked' && openDialog) {
      setOpenDialog(false);
    }
  }, [blocker.state, isFormDirty, openDialog]);

  const handleCloseDialog = useCallback(() => {
    setOpenDialog(false);
    if (blocker.state === 'blocked') {
      blocker.reset(); // Cancel the pending navigation
    }
  }, [blocker]);

  const handleConfirmLeave = useCallback(() => {
    setOpenDialog(false);
    if (onConfirmLeave) {
      onConfirmLeave();
    }
    if (blocker.state === 'blocked') {
      blocker.proceed(); // Allow the pending navigation to continue
    }
  }, [blocker, onConfirmLeave]);

  // The ConfirmLeaveDialog component is defined and returned here
  const ConfirmLeaveDialog: React.FC = () => (
    <ConfirmDialog
      open={openDialog}
      onClose={handleCloseDialog}
      onConfirm={handleConfirmLeave}
      message={confirmationMessage}
      title={dialogTitle}
      onCancel={handleCloseDialog}
      confirmLabel={dialogConfirmText}
      cancelLabel={dialogCancelText}
    ></ConfirmDialog>
  );

  return { isDirty: isFormDirty, ConfirmLeaveDialog };
}